<template>
  <slot></slot>
  <modal :id="modalId" :title="viewModeTitle" :icon="viewModeIcon" @shown="onModalShown">
    <form :id="formId" :action="action" method="POST" @submit="loading = true">
      <slot name="hidden"></slot>
      <input type="hidden" name="_token" :value="$csrf_token">
      <input type="hidden" name="_method" :value="formMethod">

      <!-- ### Main view mode -->
      <template v-if="mode('main')">

        <div class="form-group row">
          <label for="date" class="col-sm-3 col-form-label">Date & Time:</label>
          <div class="col-sm-9">
            <datepicker
              v-model="fields.date"
              :with-time="true"
              placeholder="Leave blank to use current date/time"
              name="date">
            </datepicker>
          </div>
        </div>

        <div class="form-group row">
          <label for="recovery_type" class="col-sm-3 col-form-label">Recovery Type:</label>
          <div class="col-sm-9">
            <select class="custom-select" :class="{'is-invalid': invalid('type')}" v-model="fields.type" name="recovery_type">
              <option value="">Select recovery type...</option>
              <option v-for="rType in Object.entries(types)" :value="rType[0]">{{ rType[1] }}</option>
            </select>
            <div v-if="invalid('type')" class="small text-danger">{{ message('type') }}</div>
          </div>
        </div>

        <div v-if="needSubcategory && (fields.type === laeTypeCode)" class="form-group row">
          <label for="subcategory" class="col-sm-3 col-form-label">Subcategory:</label>
          <div class="col-sm-9">
            <select class="custom-select" :class="{'is-invalid': invalid('subcategory')}" v-model="fields.subcategory" name="subcategory" required>
              <option value="">Select subcategory...</option>
              <option v-for="subcategory in Object.entries(subcategoryOptions)" :value="subcategory[0]">{{ subcategory[1] }}</option>
            </select>
            <div v-if="invalid('subcategory')" class="small text-danger">{{ message('subcategory') }}</div>
          </div>
        </div>

        <div class="form-group row">
          <label for="recovery_amount" class="col-sm-3 col-form-label">Recovery Amount:</label>
          <div class="col-sm-9">
            <div class="input-group">
              <div class="input-group-prepend" :class="{'is-invalid': invalid('amount')}">
                <span class="input-group-text"><i class="fa fa-dollar fa-fw"></i></span>
              </div>
              <input type="number" step="0.01" min="0.01"
                     class="form-control form-control-sm" :class="{'is-invalid': invalid('amount')}"
                     name="recovery_amount" v-model="fields.amount" placeholder="Enter +/- recovery amount">
            </div>
            <div v-if="invalid('amount')" class="small text-danger">{{ message('amount') }}</div>
          </div>
        </div>

        <div class="form-group row">
          <label for="note" class="col-sm-3 col-form-label">Comments:</label>
          <div class="col-sm-9">
            <textarea class="form-control" name="note" v-model="fields.note" style="resize: none;" placeholder="Enter comments related to this transaction here..." rows="4"></textarea>
          </div>
        </div>

      </template>

      <!-- ### Deletion view mode -->
      <template v-if="mode('delete')">

        <p class="text-center mb-0">Are you sure you want to delete the recovery?</p>

      </template>

    </form>

    <template #footer>
      <!-- ### Main view mode -->
      <template v-if="mode('main')">
        <button type="button" class="btn btn-danger mr-auto" v-if="allowDelete" @click="setMode('delete')" :disabled="loading"><i class="fa fa-fw fa-trash"></i> Delete</button>

        <button type="button" class="btn btn-default" @click="hideModal"><i class="fa fa-fw fa-close"></i> Close</button>

        <a v-if="invalidated" href class="btn btn-warning" @click.prevent="displayValidation"><i class="fa fa-fw fa-save"></i> Save <i class="fa fa-fw fa-exclamation-triangle"></i></a>
        <button v-else type="submit" class="btn btn-primary" :form="formId" :disabled="loading">
          <i v-if="loading" class="fa fa-spinner fa-spin fa-fw"></i>
          <i class="fa fa-fw fa-save"></i> Save
        </button>
      </template>

      <!-- ### Deletion view mode -->
      <template v-if="mode('delete')">
        <button type="button" class="btn btn-default" @click="setMode(mainViewMode)" :disabled="loading"><i class="fa fa-fw fa-ban"></i> Cancel</button>
        <button type="button" class="btn btn-danger" @click="confirmDeletion" :disabled="loading">
          <i v-if="loading" class="fa fa-spinner fa-spin fa-fw"></i>
          <i class="fa fa-fw fa-trash"></i> Delete
        </button>
      </template>
    </template>

  </modal>
</template>

<script>
import cims from "../../../cims";
import HasModal from "./../../mixins/HasModal";
import HasViewModes from "../../mixins/HasViewModes";
import HasValidation from "../../mixins/HasValidation";

export default {
  mixins: [HasModal, HasViewModes, HasValidation],

  props: {
    // Modal dialog id.
    modalId: {required: false, default: 'recoveryModal'},
    // Form action URL.
    action: {type: String, required: true},
    // Form method.
    method: {type: String, default: 'POST'},
    // Modal title.
    title: {type: String, default: "Add Recovery"},
    id: {type: Number, default: 0},
    types: {type: Object, required: true},
    needSubcategory: {type: Boolean, default: false},
    subcategoryOptions: {type: Object, default: () => {}},
    laeTypeCode: {type: String, default: "LAE"},

    // ### Form fields.
    date: {type: String, default: ''},
    type: {type: String, default: ''},
    subcategory: {type: String, default: ''},
    amount: {type: String, default: ''},
    note: {type: String, default: ''},
  },

  data() {
    return {
      fields: {
        date: this.date,
        type: this.type,
        subcategory: this.subcategory,
        amount: this.amount,
        note: this.note,
      },
      formMethod: this.method,
      viewModes: [
        {'mode': 'main',    'title': this.title,        'icon': 'fa-repeat'},
        {'mode': 'delete',  'title': 'Delete Recovery', 'icon': 'fa-trash'},
      ],
      loading: false,
    }
  },

  computed: {

    formId() {
      return this.modalId + "-form"
    },

    mainViewMode() {
      return 'main';
    },

    rules() {
      return {
        'type': {'required': this.fields.type !== ''},
        'subcategory': {
          'required': (this.needSubcategory && (this.fields.subcategory !== '') && (this.fields.type === this.laeTypeCode)) || (this.fields.type !== this.laeTypeCode) || !this.needSubcategory,
        },
        'amount': {
          'required': (this.fields.amount !== '') && (this.fields.amount !== 0) || ((this.fields.amount !== '')),
          'min': (this.fields.amount >= 0.01),
        },
      }
    },

    messages() {
      return {
        'type.required': 'The recovery type is required.',
        'subcategory.required': 'The subcategory is required if the recovery type is LAE.',
        'amount.required': 'The recovery amount is required.',
        'amount.min': 'The recovery amount is required.',
      }
    },

    allowDelete() {
      return (this.id > 0);
    },

  },

  methods: {

    onModalShown() {
      this.setMode(this.mainViewMode);
      this.validationDisplayed = false;
      cims.addDateTimePickers();
    },

    confirmDeletion() {
      this.loading = true;
      this.formMethod = 'DELETE';
      this.$nextTick(() => {
        document.getElementById(this.formId).submit();
      });
    }

  },

  mounted() {
    this.viewMode = this.mainViewMode;
  }
}
</script>

<style scoped>
.btn-outline-secondary {
  border-color: #ced4da;
}
.custom-select {
  font-size: 0.875rem;
}
</style>
